import React from 'react';
import { Task, TaskStatus, useTasks } from '@/context/TaskContext';
import { Clock, CheckCircle, Trash2, Pencil, MoreVertical, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TaskCardBadge } from './TaskCardBadge';
import { formatTaskTime } from './taskCardUtils';
import { getCardBaseClasses, getCardHeaderClasses, getCardTitleClasses, getCardDateClasses, getCardMetadataClasses, getCardActionButtonClasses } from '@/components/ui/shared-card-styles';
import { cn } from '@/lib/utils';
import { PriorityDisplay } from './PriorityDisplay';
import { TaskActiveDot } from "./TaskActiveDot";
import { useTaskDetailModal } from './TaskDetailModalContext';

// Helper: return just the color for the left border
const getStatusBarColor = (status: TaskStatus): string => {
  switch (status) {
    case 'to-do': return 'bg-yellow-500 dark:bg-yellow-400';
    case 'in-progress': return 'bg-blue-500 dark:bg-blue-400';
    case 'done': return 'bg-green-500 dark:bg-green-400';
    default: return 'bg-gray-500 dark:bg-gray-400';
  }
};

interface TaskCardCompactProps {
  task: Task;
  onSelect: (id: string) => void;
  isActive: boolean;
}

export const TaskCardCompact: React.FC<TaskCardCompactProps> = ({ 
  task, 
  onSelect, 
  isActive 
}) => {
  const { deleteTask, setTaskToEdit, updateTaskStatus } = useTasks();
  const { open } = useTaskDetailModal();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteTask(task.id);
    toast.success('Task deleted');
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTaskToEdit(task);
    toast.success('Editing task');
  };

  const handleMoveTask = (e: React.MouseEvent, newStatus: TaskStatus) => {
    e.stopPropagation();
    updateTaskStatus(task.id, newStatus);
    toast.success(`Task moved to ${newStatus.replace('-', ' ')}`);
  };

  const formattedTime = formatTaskTime(task.totalTimeSpent);

  // Single click = select, double click = open modal
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(task.id); // Only select, do NOT open modal
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    open(task); // Show detail modal
  };

  return (
    <div 
      className={cn(
        getCardBaseClasses(isActive),
        "relative overflow-hidden"
      )}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {/* Colored edge with straight inner (left) edge */}
      <div
        className={cn(
          "absolute left-0 top-0 h-full w-1.5 sm:w-2",
          getStatusBarColor(task.status),
          "rounded-none"
        )}
        aria-hidden="true"
      />
      {isActive && <TaskActiveDot />}
      
      <div className={getCardHeaderClasses()}>
        <h3 className={cn(getCardTitleClasses(), "text-sm")}>{task.title}</h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={getCardActionButtonClasses()}>
              <MoreVertical size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleEdit}>
              <Pencil size={14} className="mr-2" />
              Edit task
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDelete}>
              <Trash2 size={14} className="mr-2" />
              Delete task
            </DropdownMenuItem>
            {task.status !== 'to-do' && (
              <DropdownMenuItem onClick={(e) => handleMoveTask(e, 'to-do')}>
                <ChevronRight size={14} className="mr-2" />
                Move to To-do
              </DropdownMenuItem>
            )}
            {task.status !== 'in-progress' && (
              <DropdownMenuItem onClick={(e) => handleMoveTask(e, 'in-progress')}>
                <ChevronRight size={14} className="mr-2" />
                Move to In progress
              </DropdownMenuItem>
            )}
            {task.status !== 'done' && (
              <DropdownMenuItem onClick={(e) => handleMoveTask(e, 'done')}>
                <ChevronRight size={14} className="mr-2" />
                Move to Done
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {(task.detail || task.description) && (
        <p className={cn(getCardDateClasses(), "line-clamp-1")}>{task.detail || task.description}</p>
      )}
      <div className={getCardMetadataClasses()}>
        <div className="flex items-center">
          <CheckCircle size={14} className="mr-1" />
          <span>{task.pomodorosCompleted} pomodoros</span>
        </div>
        <div className="flex items-center">
          <Clock size={14} className="mr-1" />
          <span>{formattedTime}</span>
        </div>
      </div>
      {task.priority_quadrant && (
        <PriorityDisplay quadrant={task.priority_quadrant} />
      )}
    </div>
  );
};
